/**
 * Stable `data-testid` contract for load-bearing interactive elements.
 *
 * ## Why this exists
 *
 * E2E specs (Playwright) and the Page Object Models that wrap them must target
 * elements that survive copy edits, label tweaks, and design churn. Selecting
 * by visible text or `getByLabel` is brittle — it broke the platform-admin
 * auth suite (bug 0031) because the auth screens' labels are sibling elements,
 * not `htmlFor`-linked, and the placeholders changed. A `data-testid` is an
 * explicit, behaviour-stable hook the component and the test agree on.
 *
 * ## Convention
 *
 * - Only **load-bearing** elements get a testid: the controls a journey must
 *   drive (inputs it fills, buttons it clicks) or the landmarks it asserts on
 *   (a page's identity element, a dialog's presence). Do NOT sprinkle testids
 *   on every node.
 * - Values are namespaced `"<area>-<element>"`, kebab-case, e.g.
 *   `"login-email"`, `"sidebar-logout"`.
 * - Consume these constants from specs/POMs — never hard-code the string
 *   literal in a spec — so a rename is a single edit the type system
 *   propagates. (Product components apply the matching literal via the
 *   `data-testid` prop; every `@duvi-agency/ui` primitive and the auth
 *   primitives spread `...props`, so the attribute reaches the DOM unchanged.)
 *
 * ## Naming patterns for portal pages (story 27 expansion)
 *
 * The following conventions standardise how journey stories 28–33 address
 * portal elements. Apply these patterns when wiring up testids in product
 * components so no spec ever hard-codes a string literal.
 *
 * ### List / table rows
 * `"<page>-row"` — the repeating row container. Stories select by index or by
 * a nested query (e.g. `getByTestId("kb-row").filter({ hasText: "title" })`).
 * Example: `"kb-row"`, `"ticket-row"`, `"user-row"`, `"niche-row"`, `"audit-row"`.
 *
 * ### Action menus / overflow triggers
 * `"<page>-row-actions"` — the button/trigger that opens the row's action
 * menu. Must be unique per row; stories pass an `nth()` index.
 * Example: `"kb-row-actions"`, `"user-row-actions"`.
 *
 * ### Action menu items
 * `"<page>-action-<verb>"` — individual items inside an action menu.
 * Example: `"kb-action-delete"`, `"user-action-disable"`, `"user-action-archive"`.
 *
 * ### Confirm / alert dialogs
 * `"<subject>-dialog"` — the dialog container (matches Radix `DialogContent`).
 * `"<subject>-dialog-confirm"` — the destructive/confirm button inside.
 * `"<subject>-dialog-cancel"` — the cancel button inside.
 * Example: `"delete-kb-dialog"`, `"delete-kb-dialog-confirm"`, `"archive-user-dialog"`.
 *
 * ### Filters
 * `"<page>-filter-<field>"` — a select/combobox/multiselect that filters the
 * list. Example: `"kb-filter-category"`, `"ticket-filter-status"`.
 *
 * ### Pagination
 * `"<page>-pagination"` — the pagination control container.
 * `"<page>-pagination-prev"` — previous page button.
 * `"<page>-pagination-next"` — next page button.
 * `"<page>-pagination-info"` — the "Showing X–Y of Z" text node.
 *
 * ### Form fields (slide-overs, modals, inline forms)
 * `"<form>-<fieldname>"` — inputs inside a modal or slide-over form.
 * `"<form>-submit"` — the primary submit/save button.
 * Example: `"invite-user-email"`, `"invite-user-submit"`, `"change-password-current"`.
 *
 * ### Empty states
 * `"<page>-empty"` — the empty-state container shown when a list has no rows.
 *
 * ### Error states
 * `"<page>-error"` — the error-state container shown when a fetch fails.
 *
 * These ids are part of the package's public contract: removing or renaming
 * one is a breaking change for any consumer's E2E suite.
 */
export const testIds = {
    // --- Login: password entry (LoginPasswordEntry, @duvi-agency/auth) ---
    loginEmail: "login-email",
    loginPassword: "login-password",
    loginSubmit: "login-submit",
    /** Visible error message when credentials are rejected (wrong password etc). */
    loginError: "login-error",
    // --- Login: TOTP / MFA challenge (LoginMFAChallenge, @duvi-agency/auth) ---
    /** The 6 single-digit OTP inputs share this id; select by index in a POM. */
    mfaInput: "mfa-input",
    /** Visible error message when the TOTP code is rejected. */
    mfaError: "mfa-error",
    // --- Set new password (SetNewPasswordForm, @duvi-agency/auth) ---
    // Reached via NEW_PASSWORD_REQUIRED challenge at /auth/new-password.
    setNewPasswordName: "set-new-password-name",
    setNewPasswordNew: "set-new-password-new",
    setNewPasswordConfirm: "set-new-password-confirm",
    setNewPasswordSubmit: "set-new-password-submit",
    setNewPasswordError: "set-new-password-error",
    // --- Forgot / reset password flow (@duvi-agency/auth) ---
    // ForgotPasswordEmail (/auth/forgot-password): email input + send button.
    forgotPasswordEmail: "forgot-password-email",
    forgotPasswordSubmit: "forgot-password-submit",
    // ForgotPasswordCheckEmail (/auth/forgot-password/check-email): code entry.
    forgotPasswordCode: "forgot-password-code",
    forgotPasswordContinue: "forgot-password-continue",
    // ResetPasswordForm (/auth/reset-password): new password + confirm + submit.
    resetPasswordNew: "reset-password-new",
    resetPasswordConfirm: "reset-password-confirm",
    resetPasswordSubmit: "reset-password-submit",
    // --- App shell: sidebar user menu + logout (Sidebar, platform-admin) ---
    sidebarUserMenu: "sidebar-user-menu",
    sidebarLogout: "sidebar-logout",
    logoutConfirm: "logout-confirm",
    // --- Session expiry (SessionExpiredDialog, @duvi-agency/auth) ---
    /** Container dialog — its presence on an authed page is the bug-0057 symptom. */
    sessionExpiredDialog: "session-expired-dialog",
    sessionExpiredSignIn: "session-expired-signin",
    // --- Page identity landmarks (assertable "we are here" anchors) ---
    dashboardPage: "dashboard-page",
    kbListPage: "kb-list-page",
    kbEditorPage: "kb-editor-page",
    ticketsPage: "tickets-page",
    ticketDetailPage: "ticket-detail-page",
    userManagementPage: "user-management-page",
    userDetailPage: "user-detail-page",
    auditLogPage: "audit-log-page",
    nichesPage: "niches-page",
    settingsPage: "settings-page",
    // --- Knowledge base list (/knowledge-base) ---
    /** Repeating article card/row container. Select by index or filter by text. */
    kbRow: "kb-row",
    /** Overflow/actions trigger on an article row. */
    kbRowActions: "kb-row-actions",
    /** "Delete" item in the article row action menu. */
    kbActionDelete: "kb-action-delete",
    /** Confirmation dialog for article deletion. */
    kbDeleteDialog: "delete-kb-dialog",
    kbDeleteDialogConfirm: "delete-kb-dialog-confirm",
    kbDeleteDialogCancel: "delete-kb-dialog-cancel",
    /** Category filter combobox on the KB list. */
    kbFilterCategory: "kb-filter-category",
    /** Empty state container (no articles). */
    kbEmpty: "kb-empty",
    /** Error state container (fetch failed). */
    kbError: "kb-error",
    // --- Knowledge base editor (/knowledge-base/new + /:id) ---
    kbEditorTitle: "kb-editor-title",
    kbEditorCategory: "kb-editor-category",
    /** Tag text input (inline inside the tag chip container). Locate by testId
     *  rather than placeholder because the placeholder is empty once any tags exist. */
    kbEditorTagInput: "kb-editor-tag-input",
    kbEditorPublish: "kb-editor-publish",
    kbEditorSaveDraft: "kb-editor-save-draft",
    // --- Support tickets list (/support/tickets) ---
    /** Repeating ticket row. */
    ticketRow: "ticket-row",
    ticketFilterStatus: "ticket-filter-status",
    ticketFilterPriority: "ticket-filter-priority",
    ticketPaginationPrev: "ticket-pagination-prev",
    ticketPaginationNext: "ticket-pagination-next",
    ticketPaginationInfo: "ticket-pagination-info",
    ticketEmpty: "ticket-empty",
    // --- Support ticket detail (/support/tickets/:id) ---
    ticketReplyInput: "ticket-reply-input",
    ticketReplySubmit: "ticket-reply-submit",
    ticketInternalNoteToggle: "ticket-internal-note-toggle",
    ticketResolve: "ticket-resolve",
    ticketClose: "ticket-close",
    // --- User management (/user-management) ---
    /** Repeating user row. */
    userRow: "user-row",
    /** Overflow/actions trigger on a user row. */
    userRowActions: "user-row-actions",
    userActionDisable: "user-action-disable",
    userActionEnable: "user-action-enable",
    userActionArchive: "user-action-archive",
    userActionUnarchive: "user-action-unarchive",
    userActionForceReset: "user-action-force-reset",
    /** "Invite user" button that opens the invite slide-over. */
    userInviteButton: "user-invite-button",
    /** Invite slide-over / form fields. */
    inviteUserEmail: "invite-user-email",
    inviteUserSubmit: "invite-user-submit",
    /** Archive confirmation dialog. */
    archiveUserDialog: "archive-user-dialog",
    archiveUserDialogConfirm: "archive-user-dialog-confirm",
    archiveUserDialogCancel: "archive-user-dialog-cancel",
    /** "Show archived" toggle. */
    userShowArchived: "user-show-archived",
    userEmpty: "user-empty",
    // --- Audit log (/audit-log) ---
    /** Repeating audit event row. */
    auditRow: "audit-row",
    auditFilterActor: "audit-filter-actor",
    auditFilterAction: "audit-filter-action",
    auditFilterEntity: "audit-filter-entity",
    auditFilterDateFrom: "audit-filter-date-from",
    auditFilterDateTo: "audit-filter-date-to",
    auditExportCsv: "audit-export-csv",
    /** Detail slide-over for an audit event (before/after diff). */
    auditDetailPanel: "audit-detail-panel",
    auditEmpty: "audit-empty",
    // --- Niches list (/platform/niches) ---
    /** Repeating niche row. */
    nicheRow: "niche-row",
    nicheSearch: "niche-search",
    nichePaginationPrev: "niche-pagination-prev",
    nichePaginationNext: "niche-pagination-next",
    nichePaginationInfo: "niche-pagination-info",
    nicheEmpty: "niche-empty",
    // --- Settings (/settings) ---
    settingsNameInput: "settings-name-input",
    settingsNameSave: "settings-name-save",
    settingsEmailInput: "settings-email-input",
    settingsEmailSave: "settings-email-save",
    settingsEmailVerifyCode: "settings-email-verify-code",
    settingsEmailVerifySubmit: "settings-email-verify-submit",
    settingsAvatarUpload: "settings-avatar-upload",
    settingsAvatarRemove: "settings-avatar-remove",
    /** Security tab: sessions table. */
    settingsSessionsTable: "settings-sessions-table",
    settingsLogoutAllSessions: "settings-logout-all-sessions",
    settingsLogoutAllDialog: "settings-logout-all-dialog",
    settingsLogoutAllDialogConfirm: "settings-logout-all-dialog-confirm",
    /** Security tab: change password form. */
    changePasswordCurrent: "change-password-current",
    changePasswordNew: "change-password-new",
    changePasswordConfirm: "change-password-confirm",
    changePasswordSubmit: "change-password-submit",
    /** Security tab: remove MFA dialog. */
    removeMfaButton: "remove-mfa-button",
    removeMfaDialog: "remove-mfa-dialog",
    removeMfaPassword: "remove-mfa-password",
    removeMfaCode: "remove-mfa-code",
    removeMfaConfirm: "remove-mfa-confirm",
};
//# sourceMappingURL=test-ids.js.map